<template>
	<section id="juniperThemes" class="jc-view">
		<JuniperPageHeader back="/juniper" title="Themes" subtitle="Manage Juniper CMS themes" />
		<div class="jth-toolbar mt-2">
			<div class="jth-intro">
				Themes from GitHub (<code>jt-*</code> repos) combined with the deployed state and client usage.
				<span v-if="githubFetchedAt" class="jth-fetched">
					GitHub data fetched {{ fromNow(githubFetchedAt) }}.
				</span>
			</div>
			<button class="j-action-btn" :disabled="loading" @click="load(true)" title="Refresh GitHub data">
				<font-awesome-icon :icon="['fas', 'sync']" :spin="loading" class="me-1" />
				Reload
			</button>
		</div>

		<div v-if="githubError" class="jth-alert mt-2">
			<font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="me-1" />
			{{ githubError }}
		</div>
		<div v-if="loadError" class="jth-alert mt-2">
			<font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="me-1" />
			{{ loadError }}
		</div>

		<div v-if="loading && !themes.length" class="jth-loading mt-3">
			<font-awesome-icon :icon="['fas', 'spinner']" spin class="me-1" />
			Loading themes…
		</div>

		<div class="row g-3 mt-1">
			<div class="col-12" v-for="theme in themes" :key="theme.name">
				<div class="jth-card">
					<div class="jth-card__top">
						<div class="jth-card__identity">
							<span class="jth-name">{{ theme.name }}</span>
							<span v-if="theme.version" class="jth-version">v{{ theme.version }}</span>
							<span v-if="!theme.on_disk" class="jth-badge jth-badge--warn" title="Repo exists on GitHub but the theme folder is not on the themes storage">
								not deployed
							</span>
							<span v-else-if="!theme.repo && !theme.clients_count" class="jth-badge jth-badge--warn" title="Folder on the themes storage with no repo deploying to it and no client using it">
								unused folder
							</span>
							<span v-else-if="!theme.repo" class="jth-badge jth-badge--muted" title="No jt- repo deploys to this theme (matched by conf.json slug)">
								no repo
							</span>
							<span v-if="theme.repo && theme.repo.name !== `jt-${theme.name}`" class="jth-badge jth-badge--muted" title="GitHub repo deploying to this theme (conf.json slug)">
								{{ theme.repo.name }}
							</span>
							<a
								v-for="dup in (theme.repo && theme.repo.duplicates) || []"
								:key="dup.name"
								:href="dup.url"
								target="_blank"
								rel="noopener"
								class="jth-badge jth-badge--warn"
								:title="`${dup.name} also deploys to themes/${theme.name} (last push ${fromNow(dup.pushed_at)})`"
							>
								duplicate: {{ dup.name }}
							</a>
							<span
								v-if="theme.repo && theme.repo.last_build && theme.repo.last_build.conclusion && theme.repo.last_build.conclusion !== 'success'"
								class="jth-badge jth-badge--danger"
								:title="'Latest workflow run: ' + theme.repo.last_build.conclusion"
							>
								build {{ theme.repo.last_build.conclusion }}
							</span>
						</div>
						<button
							class="jth-card__usage"
							:class="{ 'jth-card__usage--open': expanded === theme.name }"
							:disabled="!theme.clients_count"
							@click="toggleClients(theme.name)"
						>
							<font-awesome-icon :icon="['fas', 'users']" class="me-1" />
							Used by <b>&nbsp;{{ theme.clients_count }}&nbsp;</b> client<span v-if="theme.clients_count !== 1">s</span>
							<font-awesome-icon v-if="theme.clients_count" :icon="['fas', expanded === theme.name ? 'chevron-up' : 'chevron-down']" class="ms-1" />
						</button>
					</div>

					<div class="jth-meta mt-3">
						<div class="jth-meta__group">
							<label>Last build</label>
							<span v-if="theme.repo && theme.repo.last_build && theme.repo.last_build.built_at" :title="formatFull(theme.repo.last_build.built_at)">
								{{ fromNow(theme.repo.last_build.built_at) }}
							</span>
							<span v-else class="jth-muted">—</span>
							<small v-if="theme.repo && theme.repo.last_build && theme.repo.last_build.workflow" class="jth-sub">
								{{ theme.repo.last_build.workflow }}
							</small>
						</div>
						<div class="jth-meta__group">
							<label>Last commit</label>
							<span v-if="lastCommit(theme)" :title="formatFull(lastCommit(theme).date)">
								{{ fromNow(lastCommit(theme).date) }}
							</span>
							<span v-else class="jth-muted">—</span>
							<small v-if="lastCommit(theme)" class="jth-sub" :title="lastCommit(theme).message">
								<code>{{ lastCommit(theme).sha }}</code>
								{{ lastCommit(theme).message }}
							</small>
						</div>
						<div class="jth-meta__group">
							<label>Author</label>
							<span v-if="lastCommit(theme) && lastCommit(theme).author">{{ lastCommit(theme).author }}</span>
							<span v-else class="jth-muted">—</span>
						</div>
						<div class="jth-meta__group">
							<label>Branch</label>
							<span v-if="theme.repo">{{ theme.repo.default_branch }}</span>
							<span v-else class="jth-muted">—</span>
						</div>
					</div>

					<div class="jth-card__actions mt-3" v-if="theme.repo">
						<a class="j-action-btn" :href="theme.repo.url" target="_blank" rel="noopener" title="Open repository on GitHub">
							<font-awesome-icon :icon="['fab', 'github']" />
						</a>
						<a v-if="lastCommit(theme) && lastCommit(theme).url" class="j-action-btn" :href="lastCommit(theme).url" target="_blank" rel="noopener">
							Commit
						</a>
						<a v-if="theme.repo.last_build && theme.repo.last_build.url" class="j-action-btn" :href="theme.repo.last_build.url" target="_blank" rel="noopener">
							Build log
						</a>
					</div>

					<div v-if="expanded === theme.name" class="jth-clients mt-3">
						<table class="jth-clients__table">
							<thead>
								<tr>
									<th>Client</th>
									<th>URL</th>
									<th>Status</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="client in theme.clients" :key="client.id">
									<td>
										<router-link :to="`/juniper/clients/edit/${client.id}`">{{ client.name }}</router-link>
										<span v-if="client.staging" class="jth-badge jth-badge--muted ms-1">staging</span>
									</td>
									<td class="jth-clients__url">{{ client.url }}</td>
									<td>{{ client.status || '—' }}</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import moment from 'moment'
import juniperApi from '../../api/juniperApi'

export default {
	name: 'Themes',
	data() {
		return {
			loading: false,
			loadError: null,
			themes: [],
			githubError: null,
			githubFetchedAt: null,
			expanded: null,
		}
	},
	computed: {
		...mapState(['authUser']),
		...mapGetters(['isAuth']),
	},
	created() {
		this.load()
	},
	methods: {
		async load(refresh = false) {
			this.loading = true
			this.loadError = null
			try {
				const { data } = await juniperApi.get('themes/overview', {
					params: refresh ? { refresh: 1 } : {},
				})
				this.themes = data.themes || []
				this.githubError = data.github_error || null
				this.githubFetchedAt = data.github_fetched_at || null
			} catch (error) {
				this.loadError = error.response?.data?.message || 'Failed to load themes overview'
			} finally {
				this.loading = false
			}
		},
		toggleClients(name) {
			this.expanded = this.expanded === name ? null : name
		},
		lastCommit(theme) {
			return theme.repo?.last_commit || null
		},
		fromNow(date) {
			return date ? moment(date).fromNow() : null
		},
		formatFull(date) {
			return date ? moment(date).format('MMM D, YYYY HH:mm') : ''
		},
	},
}
</script>
<style scoped>
#juniperThemes {
	margin-top: 0 !important;
	padding-bottom: 40px;
}

.jc-view {
	width: 100%;
}

.jth-toolbar {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
}

.jth-intro {
	font-size: 13px;
	color: #64748b;
}

.jth-fetched {
	color: #94a3b8;
}

.jth-alert {
	font-size: 13px;
	color: #92400e;
	background: #fffbeb;
	border: 1px solid #fde68a;
	border-radius: 10px;
	padding: 8px 12px;
}

.jth-loading {
	font-size: 13px;
	color: #64748b;
}

.jth-card {
	background: #ffffff;
	border: 1px solid #e2e8f0;
	border-radius: 14px;
	padding: 14px;
}

.jth-card__top {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
}

.jth-card__identity {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}

.jth-name {
	text-transform: uppercase;
	font-weight: 700;
	font-size: 12px;
	letter-spacing: 0.04em;
	background: #e2e8f0;
	color: #334155;
	border-radius: 999px;
	padding: 5px 10px;
}

.jth-version {
	font-size: 13px;
	font-weight: 700;
	color: #1e293b;
	background: #f8fafc;
	border: 1px solid #e2e8f0;
	padding: 4px 9px;
	border-radius: 999px;
}

.jth-badge {
	font-size: 12px;
	font-weight: 600;
	padding: 4px 9px;
	border-radius: 999px;
}

.jth-badge--warn {
	color: #92400e;
	background: #fffbeb;
	border: 1px solid #fde68a;
}

.jth-badge--danger {
	color: #b91c1c;
	background: #fef2f2;
	border: 1px solid #fecaca;
}

.jth-badge--muted {
	color: #64748b;
	background: #f8fafc;
	border: 1px solid #e2e8f0;
}

.jth-card__usage {
	font-size: 13px;
	color: #0f766e;
	background: #ecfeff;
	border: 1px solid #a5f3fc;
	padding: 5px 10px;
	border-radius: 999px;
	display: inline-flex;
	align-items: center;
	cursor: pointer;
}

.jth-card__usage:disabled {
	cursor: default;
	opacity: 0.7;
}

.jth-card__usage--open,
.jth-card__usage:not(:disabled):hover {
	background: #cffafe;
}

.jth-meta {
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 8px;
}

.jth-meta__group {
	background: #f8fafc;
	border: 1px solid #e2e8f0;
	border-radius: 10px;
	padding: 8px 10px;
	min-width: 0;
}

.jth-meta__group label {
	display: block;
	font-size: 11px;
	color: #64748b;
	margin: 0 0 2px;
}

.jth-meta__group span {
	font-size: 14px;
	font-weight: 700;
	color: #1f2937;
}

.jth-meta__group .jth-sub {
	display: block;
	font-size: 12px;
	font-weight: 400;
	color: #64748b;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.jth-muted {
	color: #94a3b8;
}

.jth-card__actions {
	display: inline-flex;
	align-items: center;
	gap: 8px;
}

.jth-clients__table {
	width: 100%;
	font-size: 13px;
	border-collapse: collapse;
}

.jth-clients__table th {
	font-size: 12px;
	font-weight: 500;
	color: #64748b;
	text-align: left;
	padding: 6px 8px;
	border-bottom: 1px solid #f1f3f5;
}

.jth-clients__table td {
	padding: 6px 8px;
	border-bottom: 1px solid #f1f3f5;
}

.jth-clients__url {
	color: #64748b;
}

@media (max-width: 900px) {
	.jth-meta {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
}
</style>
